import appApi from "@/api/app/AppApi"
import {actionTypes as incomeActionTypes} from "@/api/income/IncomeReducer"
import {getUserLocation, toDeviceTelemetry} from "@/lib/deviceInfo"
import {CAMERA_FAILURE, ENVIRONMENT_FAILURES, SENTRY_ALERT_FAILURES, classifyCameraEvent} from "@/lib/cameraFailure"
import {getWebAuthnCapabilities, toWebAuthnTelemetry} from "@/lib/webAuthnInfo"
import {EMPTY, of, from, fromEvent, merge, interval, timer} from "rxjs"
import {catchError, delay, filter, ignoreElements, map, mergeMap, switchMap, takeUntil, tap} from "rxjs/operators"
import * as Sentry from "@sentry/react"
import {actionFactory, actionTypes, timingPairs} from "@/api/app/AppReducer"
import {actionTypes as identityActionTypes} from "@/api/identity/IdentityReducer"
import identityEpics from "@/api/identity/IdentityEpics"
import incomeEpics from "@/api/income/IncomeEpics"
import uuid from "@/lib/uuid"
import {findTenantByDomain, findTenantByTenantCode} from "@/tenantConfig"
import {TOKEN_KEY, TOKEN_EXPIRES_KEY} from "@/Statics"
import version from "@/lib/version"

const LOCATION_CAPTURE_INTERVAL_MS = 4 * 60 * 1000

export const buildMetadata = (action, currState) => {
  const {type, payload = {}} = action
  switch (type) {
    case incomeActionTypes.filesValidated:
      return {fileCount: payload.fileCount}
    case incomeActionTypes.uploadIncomeDocument:
      return {fileCount: payload.files?.length}
    case incomeActionTypes.fileSizeExceeded:
      return {fileSizeMb: payload.fileSizeMb}
    case incomeActionTypes.mimeTypeValidationFailed:
      return {attemptedType: payload.attemptedType}
    case incomeActionTypes.fileSignatureValidationFailed:
      return {claimedType: payload.claimedType}
    case incomeActionTypes.uploadMethodSelected:
      return {method: payload.method}
    case actionTypes.cameraFailureClassified:
      return {
        failureType: payload.failureType,
        errorName: payload.errorName,
        elapsedMs: payload.elapsedMs,
        fastRejection: payload.fastRejection
      }
    case actionTypes.cdnLoadRetryAttempt:
      return {
        attempt: payload.attempt,
        maxAttempts: payload.maxAttempts,
        source: payload.source,
        lastError: payload.lastError
      }
    case actionTypes.sdkLoadCompleted:
      // Load duration includes retry backoff; this makes inflated durations filterable
      return {cdnRetryAttempts: currState.cdnRetryAttempts || 0}
    case actionTypes.captureLiveFaceTimedOut:
    case actionTypes.onUnexpectedCaptureLiveFaceError:
    case identityActionTypes.uploadSecureCaptureSelfieCompleted:
    case identityActionTypes.uploadSecureCaptureSelfieFailed:
      return {
        faceHintCount: currState.identityState?.faceHintCount || 0,
        perfectHintCount: currState.identityState?.perfectHintCount || 0,
        timeoutPageCount: currState.identityState?.timeoutPageCount || 0,
        attempts: currState.identityState?.attempts || []
      }
    default:
      return {}
  }
}

export const correlationFields = (currState) => ({
  uploadRequestId: currState?.tokenState?.uploadRequestId || uuid(),
  identityVerificationId: currState?.tokenState?.identityVerification?.id,
  incomeVerificationId: currState?.tokenState?.incomeVerification?.id
})

const AppEpics = {
  onAppContextMounted: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onAppContextMounted),
      switchMap(({action}) => {
        return merge(of(actionFactory.onLoadApiKey()), of(actionFactory.onLoadWebAuthnCapabilities()))
      })
    )
  },
  loadTenantConfig: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onAppContextMounted),
      switchMap(({action}) => {
        let tenantConfig = findTenantByDomain(window.location.hostname)
        // use sprinthive config as default
        if (!tenantConfig) {
          tenantConfig = findTenantByTenantCode("sh")
        }
        return of(actionFactory.onTenantConfigUpdated({tenantConfig}))
      })
    )
  },
  saveTokenToLocalStorage: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.exchangeTokenCompleted),
      filter(({action}) => action?.payload?.token),
      filter(({currState}) => currState?.deviceInfo?.features?.localStorage),
      tap(({action}) => {
        const TTL_IN_MS = 24 * 60 * 60 * 1000 // 24 hours
        const expiresAt = Date.now() + TTL_IN_MS
        window.localStorage.setItem(TOKEN_KEY, action.payload.token)
        window.localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt.toString())
      })
    )
  },
  loadTokenFromLocalStorage: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onLoadApiKeyCompleted),
      switchMap(({currState}) => {
        const tStr = "/t/"
        const tokenStr = "/token/"
        const tFound = window.location.pathname.includes(tStr)
        const tokenFound = window.location.pathname.includes(tokenStr)

        // only try and load the token from local storage when there is no token in the url path
        if (!tFound && !tokenFound) {
          if (currState?.deviceInfo?.features?.localStorage) {
            const storage = window.localStorage
            const storedToken = storage.getItem(TOKEN_KEY)
            const storedExpiresAt = storage.getItem(TOKEN_EXPIRES_KEY)

            if (storedToken && storedToken.trim().length > 0) {
              let token = storedToken
              let expiresAt = storedExpiresAt

              // Handle backward compatibility with old JSON format
              try {
                const parsedData = JSON.parse(storedToken)
                if (parsedData && typeof parsedData === 'object' && parsedData.token && parsedData.expiresAt) {
                  // Old format detected, migrate to new format
                  token = parsedData.token
                  expiresAt = parsedData.expiresAt.toString()

                  // Migrate to new storage format
                  storage.setItem(TOKEN_KEY, token)
                  storage.setItem(TOKEN_EXPIRES_KEY, expiresAt)
                }
              } catch (e) {
                // Not JSON, continue with current token as string
              }

              // Check if token has expired
              if (expiresAt && parseInt(expiresAt) < Date.now()) {
                storage.removeItem(TOKEN_KEY)
                storage.removeItem(TOKEN_EXPIRES_KEY)
                return of(actionFactory.onNoTokenFound())
              }
              return of(actionFactory.onTokenUpdated({token}))
            }
          }
          return of(actionFactory.onNoTokenFound())
        }
        return EMPTY
      })
    )
  },
  loadApiKey: (epic$, {appApi}) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onLoadApiKey),
      switchMap(({action: {payload}}) =>
        appApi.loadApiKey().pipe(
          mergeMap((res) => {
            return of(actionFactory.onLoadApiKeyCompleted(res.data))
          }),
          catchError((error) => {
            return of(actionFactory.onLoadApiKeyFailed(error))
          })
        )
      ),
      delay(3000)
    )
  },
  exchangeToken: (epic$, {appApi}) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.exchangeToken),
      filter(({action}) => action.payload?.token && action.payload?.token?.trim()?.length > 0),
      filter(({action}) => action.payload?.apiKey && action.payload?.apiKey?.trim()?.length > 0),
      switchMap(({action: {payload}}) =>
        appApi.exchangeToken(payload).pipe(
          mergeMap((res) => {
            return of(actionFactory.exchangeTokenCompleted(res.data))
          }),
          catchError((error) => {
            return of(actionFactory.exchangeTokenFailed({error, payload}))
          })
        )
      )
    )
  },
  triggerExchangeToken: (epic$) => {
    const whitelist = [actionTypes.onTokenUpdated, actionTypes.onLoadApiKeyCompleted]
    return epic$.pipe(
      filter(({action}) => whitelist.includes(action.type)),
      filter(({currState}) => currState.token !== undefined && currState.apiKey !== undefined),
      switchMap(({currState}) => {
        return of(actionFactory.exchangeToken({apiKey: currState.apiKey, token: currState.token}))
      })
    )
  },
  blockedCameraDetected: (epic$, {appApi}) => {
    return epic$.pipe(filter(({action}) => action.type === actionTypes.blockedCameraDetected)).pipe(
      switchMap(({action: {payload}, currState}) =>
        appApi.blockedCamera({payload, apiKey: currState.apiKey}).pipe(
          mergeMap((res) => {
            return of(actionFactory.blockedCameraDetectedCompleted(res.data))
          }),
          catchError((error) => {
            return of(actionFactory.blockedCameraDetectedFailed({error, payload}))
          })
        )
      )
    )
  },
  captureLocation: (epic$, {appApi}) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.captureLocationSuccess),
      mergeMap(({action: {payload}, currState}) =>
        appApi.captureLocation({payload, apiKey: currState.apiKey}).pipe(
          ignoreElements(),
          catchError(() => EMPTY)
        )
      )
    )
  },
  classifyCameraFailure: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.cameraErrorReceived),
      mergeMap(({action: {payload}, currState}) => {
        // try/catch, not catchError: the detail is SDK-constructed, and a throw here
        // would terminate every epic for the session
        try {
          const classified = classifyCameraEvent(payload?.detail)

          // One failure, one document. A same-bucket repeat is deduped outright; a repeat
          // that falls back to unknown after its observation was consumed dedupes by
          // message too, since the bucket can no longer speak for it. Any other
          // reclassification, including unknown resolving into a specific bucket, is new.
          const previous = currState?.cameraFailure
          const isSameBucket = previous?.failureType === classified.failureType
          const isConsumedDowngrade = previous &&
            previous.failureType !== CAMERA_FAILURE.UNKNOWN &&
            classified.failureType === CAMERA_FAILURE.UNKNOWN &&
            previous.message != null && previous.message === classified.message

          if (isSameBucket || isConsumedDowngrade) {
            return EMPTY
          }

          // Classification plus reset is what lands the user on the permissions page
          if (ENVIRONMENT_FAILURES.includes(classified.failureType)) {
            return of(
              actionFactory.cameraFailureClassified(classified),
              actionFactory.resetCameraInitialization()
            )
          }
          console.error("[SH-Live-Capture] Camera error:", classified.message)
          return of(
            actionFactory.cameraFailureClassified(classified),
            actionFactory.onUnexpectedCaptureLiveFaceError({
              message: `An unexpected IDLive Face Plus error has occurred, "${classified.message}"`
            })
          )
        } catch (error) {
          console.error("[SH-Live-Capture] Failed to classify camera error:", error)
          return of(
            actionFactory.cameraFailureClassified({
              failureType: CAMERA_FAILURE.UNKNOWN,
              errorName: null,
              message: null,
              elapsedMs: null,
              fastRejection: null
            }),
            actionFactory.onUnexpectedCaptureLiveFaceError({
              message: "An unexpected IDLive Face Plus error has occurred, and its detail could not be read"
            })
          )
        }
      })
    )
  },
  watchCameraPermission: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.watchCameraPermission),
      switchMap(() =>
        from(Promise.resolve().then(() => navigator.permissions.query({name: "camera"}))).pipe(
          // Change events only: Safari and Firefox report "granted" for non-persistent
          // grants, so acting on the initial value could loop the journey
          switchMap((status) => fromEvent(status, "change").pipe(
            map(() => actionFactory.cameraPermissionStateChanged({state: status.state}))
          )),
          // TypeError where the camera descriptor is unsupported: no auto-advance
          catchError(() => EMPTY),
          takeUntil(epic$.pipe(filter(({action}) => action.type === actionTypes.unwatchCameraPermission)))
        )
      )
    )
  },
  scheduleLocationCapture: (epic$) => {
    return epic$.pipe(
      filter(({action, currState}) =>
        action.type === actionTypes.exchangeTokenCompleted &&
        Boolean(currState.tenantConfig?.features?.locationCapture)
      ),
      switchMap(() =>
        timer(0, LOCATION_CAPTURE_INTERVAL_MS).pipe(
          // Tick 0 bypasses the visibility gate: the journey has only just started
          filter((tick) => tick === 0 || document.visibilityState === "visible"),
          map(() => actionFactory.captureLocationRequested())
        )
      )
    )
  },
  locationEpic: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.captureLocationRequested),
      mergeMap(({currState}) => {
        return getUserLocation().pipe(
          mergeMap((locationData) => {
            const locationWithTimestamp = {
              ...locationData,
              capturedAt: new Date().toISOString(),
              token: currState.token
            }
            return of(actionFactory.captureLocationSuccess(locationWithTimestamp))
          }),
          // A geolocation throw escaping the defer would terminate every epic for the session
          catchError(() => EMPTY)
        )
      })
    )
  },
  captureSentryErrors: (epic$) => {
    const whiteList = [
      actionTypes.onUnexpectedCaptureLiveFaceError,
      actionTypes.noConfigurationProfileFound,
      actionTypes.sdkLoadFailed
    ]
    // Permissions-routed failures never pass onError, so without this they would only
    // appear in Elasticsearch; the alert list and its rationale live in cameraFailure.js
    const isAlertingCameraFailure = (action) =>
      action.type === actionTypes.cameraFailureClassified &&
      SENTRY_ALERT_FAILURES.includes(action.payload?.failureType)

    return epic$.pipe(
      // A classified failure describes itself; everything else needs something to report
      filter(({action}) =>
        isAlertingCameraFailure(action) ||
        (whiteList.includes(action.type) && (action?.payload?.message || action?.payload?.error))),
      tap(({action}) => {
        const message = action.type === actionTypes.cameraFailureClassified
          ? `Camera failure (${action.payload.failureType}): ${action.payload.message || action.payload.errorName || "no detail"}`
          : action?.payload?.message || action?.payload?.error?.message
        Sentry.captureMessage(message, "error")
      })
    )
  },
  metricLogging: (epic$) => {
    const metricWhiteList = [
      // App metrics
      actionTypes.onNoTokenFound,
      actionTypes.exchangeTokenCompleted,
      actionTypes.exchangeTokenFailed,
      actionTypes.checkDeploymentVersionMismatch,
      // CDN metrics
      actionTypes.cdnLoadSuccess,
      actionTypes.cdnLoadRetryAttempt,
      actionTypes.cdnLoadFailedAfterRetries,
      // SDK Metrics
      actionTypes.sdkLoadCompleted,
      actionTypes.sdkLoadFailed,
      // Identity metrics
      actionTypes.captureJourneyStarted,
      actionTypes.blockedCameraDetected,
      actionTypes.cameraFailureClassified,
      actionTypes.captureLiveFaceTimedOut,
      actionTypes.captureLiveFaceFailedToLoad,
      actionTypes.onUnexpectedCaptureLiveFaceError,
      actionTypes.noConfigurationProfileFound,
      actionTypes.cameraInitializationTimedOut,
      identityActionTypes.uploadSecureCaptureSelfieCompleted,
      identityActionTypes.uploadSecureCaptureSelfieFailed,
      identityActionTypes.onTrySecureCaptureSelfieAgainClicked,
      identityActionTypes.unexpectedErrorTryAgainClicked,
      identityActionTypes.manualCaptureSelected,
      // Income metrics
      incomeActionTypes.onUserIdentifiedLandingBypassSelected,
      incomeActionTypes.uploadIncomeDocumentCompleted,
      incomeActionTypes.uploadIncomeDocumentFailed,
      incomeActionTypes.bankSelected,
      // Income document upload validation metrics
      incomeActionTypes.filesValidated,
      incomeActionTypes.fileSizeExceeded,
      incomeActionTypes.mimeTypeValidationFailed,
      incomeActionTypes.fileSignatureValidationFailed,
      incomeActionTypes.duplicateFileDetected,
      incomeActionTypes.uploadMethodSelected,
      incomeActionTypes.fileRemovedAfterSelection,
      incomeActionTypes.uploadIncomeDocument,
      // Identifier journey selection metrics
      actionTypes.recordedIdentifierSelected,
      actionTypes.manualIdentifierSelected
    ]

    // One end-action can complete several pairs, best group by end-action.
    const timingPairsByEnd = timingPairs.reduce((acc, pair) => {
      const pairs = acc[pair.endAction] || []
      pairs.push(pair)
      acc[pair.endAction] = pairs
      return acc
    }, {})

    const metrics$ = epic$.pipe(
      filter(({action}) => metricWhiteList.includes(action.type)),
      switchMap(({action, currState}) => {
        const {type} = action
        const failure = action?.payload?.error ?? action?.payload
        const errorCode = failure?.response?.data?.error?.errors?.[0]?.errorCode

        const payload = {
          ...correlationFields(currState),
          timestamp: new Date().toISOString(),
          [type]: true,
          deviceInfo: {
            ...toDeviceTelemetry(currState.deviceInfo),
            webAuthn: toWebAuthnTelemetry(currState.webAuthnCapabilities)
          },
          errorCode,
          ...buildMetadata(action, currState),
        }
        return of({payload, apiKey: currState.apiKey})
      })
    )

    const timing$ = epic$.pipe(
      filter(({action}) => timingPairsByEnd[action.type]),
      mergeMap(({action, currState}) => {
        const {type} = action
        // Same metadata as the event document, so a duration can be filtered on its own
        const metadata = buildMetadata(action, currState)
        const payloads = timingPairsByEnd[type]
          .map(({startAction, metricName}) => {
            const start = currState.timingMap[startAction]
            const end = currState.timingMap[type]
            // Skip a pair whose start never fired, so we never log NaN.
            if (start === undefined || end === undefined) return null
            return {
              ...correlationFields(currState),
              timestamp: new Date().toISOString(),
              [metricName]: end - start,
              ...metadata
            }
          })
          .filter(Boolean)
        return of(...payloads.map(payload => ({payload, apiKey: currState.apiKey})))
      })
    )

    return merge(metrics$, timing$).pipe(
      mergeMap(({payload, apiKey}) =>
        appApi.logUserJourneyEvent({payload, apiKey}).pipe(
          mergeMap((res) => of(actionFactory.logUserJourneyEventCompleted(res.data))),
          catchError((error) => of(actionFactory.logUserJourneyEventFailed({error, payload})))
        )
      )
    )
  },
  loadWebAuthnCapabilitiesEpic: (epic$) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onLoadWebAuthnCapabilities),
      switchMap(() =>
        getWebAuthnCapabilities().pipe(
          mergeMap((capabilities) => {
            return of(actionFactory.onLoadWebAuthnCapabilitiesCompleted(capabilities))
          }),
          catchError((error) => {
            return of(actionFactory.onLoadWebAuthnCapabilitiesFailed({error: error.message}))
          })
        )
      )
    )
  },
  checkDeploymentVersion: (epic$, {appApi}) => {
    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.checkDeploymentVersion),
      switchMap(() =>
        appApi.getVersion().pipe(
          mergeMap((res) => {
            const serverVersion = res.data.version
            const clientVersion = version

            if (serverVersion !== clientVersion) {
              return of(actionFactory.checkDeploymentVersionMismatch({
                serverVersion,
                clientVersion
              }))
            }

            return of(actionFactory.checkDeploymentVersionCompleted())
          }),
          catchError((error) => {
            return of(actionFactory.checkDeploymentVersionFailed({error: error.message}))
          })
        )
      )
    )
  },
  periodicVersionCheck: (epic$) => {
    const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

    return epic$.pipe(
      filter(({action}) => action.type === actionTypes.onAppContextMounted),
      switchMap(() =>
        interval(VERSION_CHECK_INTERVAL_MS).pipe(
          filter(() => document.visibilityState === 'visible'),
          mergeMap(() => of(actionFactory.checkDeploymentVersion()))
        )
      )
    )
  },
}

// convert object into an array of functions
export default Object.keys(AppEpics)
  .map((k) => AppEpics[k])
  .concat(identityEpics)
  .concat(incomeEpics)